import { ReactNode, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { hasHover } from "@/lib/utils/pointer";
import { useOverflowing } from "./useOverflowing";

type OverflowTooltipProps = {
  text?: string;
  children: ReactNode;
  className?: string;
  /**
   * The bubble says `text` itself, not the rendered text — for a PART of a
   * longer label that must show the whole label when it is cut (the guest
   * list's seat chip cuts its place first: seats.io Teil 2, Zug 3, audit r1
   * FX-6). Off by default: every other cell keeps the rendered text.
   */
  preferText?: boolean;
  /** Passed to `useOverflowing`: a cut descendant counts as overflow (the seat chip, FY-5). */
  cutSelector?: string;
};

export const OverflowTooltip: React.FC<OverflowTooltipProps> = ({
  text,
  children,
  className,
  preferText = false,
  cutSelector,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  // `children` in the deps on purpose — see useOverflowing: the RENDERED label
  // can change under an unchanged box, and freezing the verdict there would
  // leave a truncated value permanently unreadable.
  const overflowing = useOverflowing(ref, [children, text], cutSelector);
  const [hovering, setHovering] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  // Tooltip text mirrors what is actually rendered in the cell (e.g. the
  // locale-formatted "31.07.2026 (12:30 Uhr)") instead of the raw value the
  // `text` prop carries (e.g. the ISO string "2026-07-31T10:30:00Z"). Falls
  // back to `text` for cells whose content has no readable text node.
  const [tipText, setTipText] = useState<string | undefined>(text);

  // Gate on the caller-provided `text` only — keep the set of cells that get a
  // tooltip unchanged; this commit fixes the tooltip *content*, not its reach.
  const hasTooltip = !!text;

  // useLayoutEffect (not useEffect): commit pos + the formatted tipText
  // before paint, so the tooltip's first visible frame already shows the
  // rendered text. Correctness then no longer leans solely on the `pos`
  // null-gate suppressing a one-frame raw-value flash.
  useLayoutEffect(() => {
    if (!hovering || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setPos({ top: rect.top, left: rect.left + rect.width / 2 });
    // Skip the DOM read while a descendant is focused — an open edit dropdown
    // (SINGLE_SELECT / BOOLEAN OptionInput etc.) renders its option <ul> as a
    // fixed-positioned but still-descendant node, so `textContent` would
    // otherwise concatenate every option label into the tooltip.
    const editingInside = ref.current.contains(document.activeElement);
    const rendered = editingInside || preferText ? "" : ref.current.textContent?.trim();
    setTipText(rendered || text);
  }, [hovering, text, preferText]);

  const show = hovering && overflowing && hasTooltip;

  // Post-render horizontal clamp: cells near a viewport edge would otherwise
  // push the center-anchored bubble off-screen (375px phones especially).
  // Converges in one pass — the second run computes the same clamped value.
  const tipRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!show || !tipRef.current || !pos) return;
    const margin = 8;
    const half = tipRef.current.offsetWidth / 2;
    const clamped = Math.max(
      margin + half,
      Math.min(pos.left, window.innerWidth - margin - half),
    );
    if (clamped !== pos.left) setPos({ top: pos.top, left: clamped });
  }, [show, pos, tipText]);

  return (
    <>
      <div
        ref={ref}
        className={`truncate ${className ?? ""}`}
        // The bubble is hover-gated (see below), and iPadOS reports
        // `hover: none` even with a trackpad — without this the truncated value
        // would be unreadable there with no way to get at it.
        //
        // Gated on `overflowing`, NOT merely on "not currently showing the
        // bubble": every table cell renders through here, so the looser test
        // put a native tooltip on cells whose text was fully visible anyway.
        title={overflowing && !show && tipText ? tipText : undefined}
        // Hover-gated: on a touch device a tap synthesizes mouseenter BEFORE
        // the click, and mounting the tooltip portal from that handler changes
        // the DOM mid-tap — which can swallow the click the user intended
        // (option rows in OptionInput wrap their label in this).
        onMouseEnter={() => hasHover() && setHovering(true)}
        onMouseLeave={() => setHovering(false)}
      >
        {children}
      </div>
      {show &&
        pos &&
        createPortal(
          <div
            ref={tipRef}
            style={{
              top: `${pos.top - 8}px`,
              left: `${pos.left}px`,
              transform: "translate(-50%, -100%)",
            }}
            className="fixed z-[60] surface-primary shadow-lg rounded-lg px-3 py-2 border max-w-[min(24rem,calc(100vw-1rem))] break-words pointer-events-none outline-quaternary"
          >
            <p className="body-3-regular font-secondary">{tipText}</p>
          </div>,
          document.body,
        )}
    </>
  );
};
